import Protomux from 'protomux'
import c from 'compact-encoding'
import b4a from 'b4a'
import z32 from 'z32'
import { domainHash, seal, sealOpen, sign, verify, toBoxPublicKey } from '@pine/crypto'
import { Emitter } from './emitter.js'

const PROTOCOL = 'pine/inbox/1'
const frame = { preencode: c.buffer.preencode, encode: c.buffer.encode, decode: c.buffer.decode }

/** See pairing.js — an onmessage handler must never return a pending promise. */
function detach (fn) {
  return (...args) => { Promise.resolve().then(() => fn(...args)).catch(() => {}) }
}

/** Anyone who knows your account id can find this topic — that is the point. */
export function inboxTopic (accountPublicKey) {
  return domainHash('pine/inbox/v1', accountPublicKey)
}

const letterEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.from)
    c.string.preencode(state, m.name)
    c.string.preencode(state, m.invite)
    c.string.preencode(state, m.note)
    c.uint.preencode(state, m.at)
    c.fixed64.preencode(state, m.signature)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.from)
    c.string.encode(state, m.name)
    c.string.encode(state, m.invite)
    c.string.encode(state, m.note)
    c.uint.encode(state, m.at)
    c.fixed64.encode(state, m.signature)
  },
  decode (state) {
    return {
      from: c.fixed32.decode(state),
      name: c.string.decode(state),
      invite: c.string.decode(state),
      note: c.string.decode(state),
      at: c.uint.decode(state),
      signature: c.fixed64.decode(state)
    }
  }
}

function letterSignable (m) {
  return domainHash('pine/inbox/letter/v1', m.from, m.name, m.invite, m.note, String(m.at))
}

/**
 * A personal drop box.
 *
 * Without one, the only way to start a conversation is to hand someone an
 * invite link out of band. With one, "add by Pine ID" works the way people
 * expect: the sender finds the recipient's topic on the DHT and leaves a
 * sealed invite that only the recipient can open.
 *
 * The tradeoff is that the topic is derived from a public account id, so
 * anyone holding your id can reach you — exactly like a phone number. Requests
 * therefore land in a pending queue, never straight into the chat list.
 */
export class Inbox extends Emitter {
  constructor (client) {
    super()
    this.client = client
    this.topic = inboxTopic(client.identity.publicKey)
    this.channels = new Set()
    this.muxes = new Set()
    this.byMux = new Map() // connection -> this inbox's channel on it
    this._detach = null
    this.started = false
  }

  async start () {
    if (this.started) return
    this.started = true
    this._detach = this.client.transport.onConnection((conn) => this._attach(conn))
    for (const conn of this.client.transport.connections) this._attach(conn)
    this.client.transport.join(this.topic, { server: true, client: false })
    // Announcing can take a DHT round trip, or forever while offline. Startup
    // must not wait on it — the inbox works the moment the swarm is up.
    this.client.transport.announced(this.topic)
      .then(() => this.emit('listening'))
      .catch(() => {})
  }

  async stop () {
    if (!this.started) return
    this.started = false
    if (this._detach) this._detach()
    for (const mux of this.muxes) {
      try { mux.unpair({ protocol: PROTOCOL, id: this.topic }) } catch {}
    }
    this.muxes.clear()
    for (const ch of this.channels) {
      try { ch.close() } catch {}
    }
    this.channels.clear()
    this.byMux.clear()
    await this.client.transport.leave(this.topic).catch(() => {})
  }

  _attach (stream) {
    const mux = Protomux.from(stream)
    this.muxes.add(mux)
    mux.pair({ protocol: PROTOCOL, id: this.topic }, () => this._open(mux))
    this._open(mux)
  }

  _open (mux) {
    const channel = mux.createChannel({
      protocol: PROTOCOL,
      id: this.topic,
      onclose: () => {
        this.channels.delete(channel)
        if (this.byMux.get(mux) === channel) this.byMux.delete(mux)
      }
    })
    if (!channel) return // already open on this connection
    this.byMux.set(mux, channel)

    channel.addMessage({
      encoding: frame,
      onmessage: detach((buf) => this._onLetter(buf))
    })
    channel.open()
    this.channels.add(channel)
  }

  /**
   * A letter to this account's other devices, over the channel this inbox
   * already holds on that connection — see deliverLetter() for why it cannot
   * open one of its own. False while that channel is not open yet.
   */
  sendOn (mux, sealed) {
    const channel = this.byMux.get(mux)
    if (!channel || !channel.opened) return false
    channel.messages[0].send(sealed)
    return true
  }

  _onLetter (buf) {
    const raw = sealOpen(buf, this.client.identity.boxPublicKey, this.client.identity.boxSecretKey)
    if (!raw) return

    let letter
    try {
      letter = letterEncoding.decode({ start: 0, end: raw.byteLength, buffer: raw })
    } catch {
      return
    }
    if (!verify(letterSignable(letter), letter.signature, letter.from)) return
    if (Math.abs(Date.now() - letter.at) > 7 * 24 * 60 * 60 * 1000) return

    this.emit('letter', {
      from: z32.encode(letter.from),
      name: letter.name,
      invite: letter.invite,
      note: letter.note,
      at: letter.at
    })
  }
}

/**
 * Deliver one sealed invite to an account id. Resolves as soon as a peer on
 * that topic acknowledges the channel; the recipient must be online.
 */
export async function deliverLetter (client, recipientKey, { invite, note = '' }, { timeout = 30000 } = {}) {
  const recipient = typeof recipientKey === 'string' ? z32.decode(recipientKey) : recipientKey
  const topic = inboxTopic(recipient)

  const letter = {
    from: client.identity.publicKey,
    name: client.identity.profile.name || '',
    invite,
    note,
    at: Date.now(),
    signature: null
  }
  letter.signature = sign(letterSignable(letter), client.identity.secretKey)

  const state = c.state()
  letterEncoding.preencode(state, letter)
  state.buffer = b4a.allocUnsafe(state.end)
  letterEncoding.encode(state, letter)
  const sealed = seal(state.buffer, toBoxPublicKey(recipient))

  // Addressed to this very account: see open() below.
  const own = !!(client.inbox && client.inbox.topic && b4a.equals(client.inbox.topic, topic))

  return new Promise((resolve, reject) => {
    const channels = new Set()
    let delivered = false
    let retry = null

    const finish = (ok, err) => {
      if (delivered) return
      delivered = true
      clearTimeout(timer)
      // Stopped here rather than on its own next tick: for up to three seconds
      // after a delivery settled, the retry went on poking a transport the app
      // may already be tearing down, and threw where nothing was listening.
      if (retry) clearInterval(retry)
      stopListening()
      // The pairing on our own topic is the inbox's, and stays.
      for (const mux of own ? [] : muxes) {
        try { mux.unpair({ protocol: PROTOCOL, id: topic }) } catch {}
      }
      for (const ch of channels) {
        try { ch.close() } catch {}
      }
      client.transport.leave(topic).catch(() => {})
      if (ok) resolve(true)
      else reject(err || new Error('Could not reach that person. They need Pine open to receive a request.'))
    }

    const timer = setTimeout(() => finish(false), timeout)

    const muxes = new Set()

    // pair() before opening, for the same reason the pairing channels do it:
    // a channel opened for a protocol the other side has not created yet is
    // rejected and never retried. That only happens when the two are already
    // connected — which, for an invite to somebody you share a room with, is
    // every time. The letter simply never arrived, and the app said the other
    // person was not online.
    const attach = (stream) => {
      const mux = Protomux.from(stream)
      muxes.add(mux)
      if (!own) mux.pair({ protocol: PROTOCOL, id: topic }, () => open(mux))
      open(mux)
    }

    const sentOn = new Set()
    const open = (mux) => {
      if (delivered || sentOn.has(mux)) return
      /*
       * A letter to our own account — another device of ours.
       *
       * Both devices listen on the same inbox topic, and a connection carries
       * one channel per protocol and topic, so between two of our own devices
       * that channel is already there: it is the two inboxes, talking to each
       * other. A second one cannot be opened beside it, and the letter used to
       * wait for a channel that never came. It goes over the inbox's instead;
       * the far end reads it like any other.
       */
      if (own) {
        if (client.inbox.sendOn(mux, sealed)) {
          sentOn.add(mux)
          setTimeout(() => finish(true), 500)
        }
        return
      }
      const channel = mux.createChannel({
        protocol: PROTOCOL,
        id: topic,
        onopen: () => {
          try {
            message.send(sealed)
            setTimeout(() => finish(true), 500)
          } catch (err) {
            finish(false, err)
          }
        },
        onclose: () => channels.delete(channel)
      })
      if (!channel) return
      const message = channel.addMessage({ encoding: frame })
      channel.open()
      channels.add(channel)
    }

    const stopListening = client.transport.onConnection((conn) => attach(conn))
    for (const conn of client.transport.connections) attach(conn)
    client.transport.join(topic, { server: false, client: true })
    retry = setInterval(() => {
      if (delivered) return clearInterval(retry)
      client.transport.refresh(topic)
      for (const conn of client.transport.connections) attach(conn)
    }, 3000)
    if (retry.unref) retry.unref()
  })
}
